/* eslint-disable */
// @ts-nocheck

import { CheckCircleIcon, CircleXIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import APIServices from '@/api/APIServices'
import { getProviders } from '@/api/providersAPI'
import { getErrorMessage } from '@/utils/errorHandlers'
import Select from '@/components/forms/Select'
import SpinnerLoading from '@/components/misc/SpinnerLoading'
import { InputForm } from '@/components/new/form/InputForm'
import { ToggleForm } from '@/components/new/form/ToggleForm'
import { useModalWizardStepsContext } from '@/components/new/modalWizardSteps/ModalWizardStepsContext'
import { useModalWizardTabsContext } from '@/components/new/modalWizardTabs/ModalWizardTabsContext'
import { Label } from '@/components/ui/label'
import useContainerWizzardSteps, {
  Selector,
} from '@/state/useContainerWizzardSteps'

import OriginDestinyManager from '../../../components/OriginDestinyManager'
import { SelectFormOriginDestiny } from '../../../components/SelectFormOriginDestiny'
import { SelectFormProviders } from '../../../components/SelectFormProviders'
import { useContainerDetails } from '../../../hooks/useContainerDetails'
import { IContainer } from '../../../interfaces/interfaces'

export default function DetailsAdvancedContainerTab({
  isEditing,
  editContainer,
}: {
  isEditing?: boolean
  editContainer?: IContainer
}) {
  const context = isEditing
    ? useModalWizardTabsContext()
    : useModalWizardStepsContext()
  const { useFormWizzard } = context
  const { control, setValue, getValues, watch } = useFormWizzard

  const data = useContainerWizzardSteps((state) => state.data)
  const updateData = useContainerWizzardSteps((state) => state.updateData)
  const queryClient = useQueryClient()

  // Use shared container details hook
  const { containerDetails, isLoading } = useContainerDetails(editContainer?.id)

  // Queries for async data
  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: () =>
      getProviders({
        page: 1,
        per_page: 10,
        search: '',
      }),
    staleTime: 5 * 60 * 1000,
  })

  // Async fetchers for AsyncSelectForm
  const fetchProviders = async (
    search: string
  ): Promise<{ id: string; label: string }[]> => {
    const response = await getProviders({ page: 1, per_page: 10, search })
    if (!Array.isArray(response)) return []
    return response.map(({ id, name }) => ({
      id: String(id),
      label: name,
    }))
  }

  // Set form values when container details are loaded
  useEffect(() => {
    if (containerDetails && isEditing) {
      // Para AsyncSelectForm, el valor debe coincidir con el id del option
      if (containerDetails.origin && containerDetails.origin.id) {
        setValue('origin', containerDetails.origin.id)
        setSelectedOrigin(containerDetails.origin) // También setear el estado local
      }

      if (containerDetails.destiny && containerDetails.destiny.id) {
        setValue('destiny', containerDetails.destiny.id)
        setSelectedDestiny(containerDetails.destiny) // También setear el estado local
      }

      setValue('netWeight', containerDetails.netWeight)
      setValue('grossWeight', containerDetails.grossWeight)
      setValue('code', containerDetails.code)
      setValue('sealNumber', containerDetails.sealNumber)
      setValue('isRefrigered', containerDetails.isRefrigered)
      setValue('temperatureRange', containerDetails.temperatureRange)
      setValue('trackingNumber', containerDetails.trackingNumber)
    }
  }, [containerDetails, isEditing, setValue])

  // Mutation for updating container advanced details
  const updateContainerAdvancedDetails = useMutation({
    mutationFn: async (detailsData: {
      originId?: number
      destinyId?: number
      netWeight?: number
      grossWeight?: number
      code?: string
      sealNumber?: string
      isRefrigered?: boolean
      temperatureRange?: string
      trackingNumber?: string
    }) => {
      if (!editContainer?.id) throw new Error('Container ID is required')
      const response = await APIServices.patch(
        `/cargo/containers/${editContainer.id}/details`,
        detailsData
      )
      return response.data
    },
    onSuccess: () => {
      toast.success(
        'Información de detalles avanzados actualizada correctamente'
      )
      queryClient.invalidateQueries({
        queryKey: ['container_details', editContainer?.id],
      })
      // No cerrar modal automáticamente en modo edición
    },
    onError: (error: any) => {
      const errorMessage = getErrorMessage(error)
      toast.error(
        errorMessage ||
          'Error al actualizar la información de detalles avanzados'
      )
    },
  })

  // Function to handle saving advanced details data
  const handleSaveAdvancedDetails = async () => {
    const formValues = getValues()

    const detailsData: {
      originId?: number
      destinyId?: number
      netWeight?: number
      grossWeight?: number
      code?: string
      sealNumber?: string
      isRefrigered?: boolean
      temperatureRange?: string
      trackingNumber?: string
    } = {}

    // Only include fields that have values
    if (formValues.origin) {
      detailsData.originId =
        typeof formValues.origin === 'string'
          ? Number(formValues.origin)
          : typeof formValues.origin === 'number'
            ? formValues.origin
            : Number(formValues.origin.id)
    }
    if (formValues.destiny) {
      detailsData.destinyId =
        typeof formValues.destiny === 'string'
          ? Number(formValues.destiny)
          : typeof formValues.destiny === 'number'
            ? formValues.destiny
            : Number(formValues.destiny.id)
    }
    if (formValues.netWeight) {
      detailsData.netWeight = Number(formValues.netWeight)
    }
    if (formValues.grossWeight) {
      detailsData.grossWeight = Number(formValues.grossWeight)
    }
    if (formValues.code) {
      detailsData.code = formValues.code
    }
    if (formValues.sealNumber) {
      detailsData.sealNumber = formValues.sealNumber
    }
    if (formValues.isRefrigered === true) {
      detailsData.isRefrigered = true
    }
    if (formValues.temperatureRange) {
      detailsData.temperatureRange = formValues.temperatureRange
    }
    if (formValues.trackingNumber) {
      detailsData.trackingNumber = formValues.trackingNumber
    }

    await updateContainerAdvancedDetails.mutateAsync(detailsData)
  }

  // Expose the save function to the parent component
  useEffect(() => {
    if (isEditing) {
      // @ts-ignore - Adding custom property to window for parent access
      window.handleSaveAdvancedDetails = handleSaveAdvancedDetails
      // @ts-ignore - Expose loading state
      window.isSavingAdvancedDetails = updateContainerAdvancedDetails.isPending
    }

    return () => {
      // @ts-ignore
      delete window.handleSaveAdvancedDetails
      // @ts-ignore
      delete window.isSavingAdvancedDetails
    }
  }, [
    isEditing,
    handleSaveAdvancedDetails,
    updateContainerAdvancedDetails.isPending,
  ])

  // Watch form changes and update data - using a single useEffect to avoid infinite loops
  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name) {
        updateData({ ...data, [name]: value[name] })
      }
    })

    return () => subscription.unsubscribe()
  }, [watch, updateData])

  // Estado para el valor seleccionado de origen y destino (para el modal)
  const [selectedOrigin, setSelectedOrigin] = useState<
    { id: number; name: string } | undefined
  >(undefined)
  const [selectedDestiny, setSelectedDestiny] = useState<
    { id: number; name: string } | undefined
  >(undefined)

  return (
    <div className="h-[60dvh] overflow-y-auto">
      <div
        className={'space-y-5'}
        style={isEditing ? { marginTop: '-.5rem' } : {}}
      >
        {/* Origen */}
        <div className="flex items-end gap-2">
          <div className="w-full">
            <SelectFormOriginDestiny
              control={control}
              label="Origen"
              name="origin"
              notFoundMessage="No se encontraron orígenes."
              placeholder="Buscar origen..."
            />
          </div>
          <div className="ml-2 shrink-0">
            <OriginDestinyManager
              onSelect={(item) => {
                setValue('origin', { id: item.id, name: item.name })
                setSelectedOrigin(item)
              }}
              selectedValue={selectedOrigin}
              type="origin"
            />
          </div>
        </div>

        {/* Destino */}
        <div className="flex items-end gap-2">
          <div className="w-full">
            <SelectFormOriginDestiny
              control={control}
              label="Destino"
              name="destiny"
              notFoundMessage="No se encontraron destinos."
              placeholder="Buscar destino..."
            />
          </div>
          <div className="ml-2 shrink-0">
            <OriginDestinyManager
              onSelect={(item) => {
                setValue('destiny', { id: item.id, name: item.name })
                setSelectedDestiny(item)
              }}
              selectedValue={selectedDestiny}
              type="destiny"
            />
          </div>
        </div>

        {/* Peso neto */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <InputForm
              control={control}
              defaultValue={containerDetails?.netWeight}
              label="Peso neto"
              name="netWeight"
              placeholder="Peso neto"
              type="number"
            />
          </div>
        </div>

        {/* Peso bruto */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <InputForm
              control={control}
              defaultValue={containerDetails?.grossWeight}
              label="Peso bruto"
              name="grossWeight"
              placeholder="Peso bruto"
              type="number"
            />
          </div>
        </div>

        {/* Código */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <InputForm
              control={control}
              defaultValue={containerDetails?.code}
              label="Código"
              name="code"
              placeholder="Código"
            />
          </div>
        </div>

        {/* Sello */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <InputForm
              control={control}
              defaultValue={containerDetails?.sealNumber}
              label="Sello"
              name="sealNumber"
              placeholder="Número de sello"
            />
          </div>
        </div>

        {/* Es refrigerado */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <ToggleForm
              control={control}
              defaultValue={containerDetails?.isRefrigered}
              label="Es refrigerado"
              name="isRefrigered"
            />
          </div>
        </div>

        {/* Rango de temperatura */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <InputForm
              control={control}
              defaultValue={containerDetails?.temperatureRange}
              label="Rango de temperatura"
              name="temperatureRange"
              placeholder="Rango de temperatura"
            />
          </div>
        </div>

        {/* Tracking */}
        <div className="flex items-center gap-2">
          <div className="w-full">
            <InputForm
              control={control}
              defaultValue={containerDetails?.trackingNumber}
              label="Tracking"
              name="trackingNumber"
              placeholder="Número de tracking"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
